import { createFileRoute, useMatch, Link, Outlet, useParams } from '@tanstack/react-router'
import { CRUMB_LINK_CLASS } from './-components/title-section'
import { trpc } from '@/main'
import { Suspense } from 'react'
import { noop, useSuspenseQuery } from '@tanstack/react-query'
import { useLingui } from '@lingui/react/macro'
import { clearProfileSelection, useRootSearch } from '@/hooks/use-root-search'
import { usePermissions } from '@/hooks/use-permissions'
import { UsersIcon, FileTextIcon, ChevronLeftIcon } from 'lucide-react'
import { ButtonLink } from '@/components/ui/button'
import { NAVIGATION_ICONS } from './-components/shared/navigation-config'
import { SectionIconTile } from './-components/shared/section-icon'
import {
  usePreviewMode,
  useIsLivePane,
  NavStackStage,
  NavStackScreen,
  NS_PASSTHROUGH_CLASS,
} from '@/components/custom/route-transition'
import { SectionStack } from './-components/shared/section-stack'
import { ReportScreenTitle } from './-components/report/report-screen-title'
import { MembersPanelSkeleton, ReportPanelSkeleton } from './-components/app-layout-skeleton'
import { ListBodyPending } from './-components/shared/list-body-pending'
import { ListPanePending } from './-components/shared/list-pane-pending'
import {
  PageLayout,
  StickyPanel,
  ScrollableContent,
  ScrollableArea,
  SlideInPanel,
  CollapsibleListPanel,
} from './-components/shared/layout'
import {
  CollapsiblePanelProvider,
  CollapsiblePanel,
  CollapsiblePanelContent,
} from './-components/shared/collapsible-panel'
import { PanelEmptyState, PanelSelectionPrompt } from './-components/shared/panel-empty-state'
import { orgGroupDetail } from './-components/orgs/org-group-detail'
import { OrgsGroupsOverview } from './-components/orgs/orgs-groups-overview'
import { OrgGroupActionMenu } from './-components/orgs/org-group-action-menu'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'

const OrgsUsersPanel = orgGroupDetail.pane((panes) => panes.OrgsUsersPanel)
const OrgGroupReportContent = orgGroupDetail.pane((panes) => panes.OrgGroupReportContent)
const ReportPager = orgGroupDetail.pane((panes) => panes.ReportPager)

export const Route = createFileRoute('/_app/orgs')({
  context: () => ({
    orgsListQueryOptions: trpc.orgs.getOrgs.queryOptions(),
  }),
  // No default-org groups here: the groups rail only renders under an `$orgId`, whose own loader warms that key.
  loader: ({ context }) => {
    context.queryClient.query(context.orgsListQueryOptions).catch(noop)
  },
  component: RouteComponent,
  pendingComponent: ListPanePending,
  // Painted on the click frame; safe because nothing in the URL keys this match (no `loaderDeps`), so it never
  // re-keys under the reader.
})

function RouteComponent() {
  const isLivePane = useIsLivePane()
  const { t } = useLingui()
  const { groupId } = useParams({ strict: false })
  // Orgs access is the static `orgs:read` capability (see usePermissions / canAccessOrgs). The nav link is
  // hidden for anyone without it, but a direct URL hit would otherwise reach the panels' getOrgs suspense
  // query and hard-crash on the backend's INSUFFICIENT_PERMISSIONS — so degrade to a no-access state here.
  const canAccessOrgs = usePermissions().can('orgs:read')
  const titleActions = canAccessOrgs && groupId ? <OrgGroupActionMenu group={{ id: groupId }} variant="header" /> : null

  // Inside a report-tab live preview (/orgs/<id>/groups/<gid>/board …), strip to just the pager pane.
  if (usePreviewMode('/orgs') === 'passthrough') {
    return (
      <Suspense fallback={null}>
        <ReportPager className="h-full" />
      </Suspense>
    )
  }

  return (
    <div id={isLivePane ? 'report' : undefined} className="@container flex h-full flex-col">
      {/* On a phone the three panes of a selected administration are STACKED SCREENS, not a squashed row:
          the groups list, the group's users, and the report, each pushed in from the right over the last
          (see nav-stack.tsx). The provider sits above PageLayout so the title bar can carry the stack's
          back chevron. The /orgs overview itself stays outside the stack — it is a full-page route. */}
      <OrgsStack>
        <PageLayout
          icon={NAVIGATION_ICONS.organizations}
          title={t`Administrations`}
          breadcrumbsSlot={canAccessOrgs ? <OrgsBreadcrumbs /> : null}
          titleActions={titleActions}
        >
          {canAccessOrgs ? (
            <OrgsLayout />
          ) : (
            <ScrollableContent>
              <ScrollableArea>
                <PanelEmptyState
                  icon={<NAVIGATION_ICONS.organizations className="text-muted-foreground size-5" />}
                  title={t`No access`}
                  description={t`You don't have permission to access this section.`}
                />
              </ScrollableArea>
            </ScrollableContent>
          )}
        </PageLayout>
      </OrgsStack>
    </div>
  )
}

/**
 * The mobile hierarchy of a selected administration — groups → the group's users → its report. The standard
 * section shape one level in, so the whole declaration is the three routes involved; `SectionStack` owns the
 * depth derivation and the pop policy. Outside an administration (the /orgs overview) there is no stack at
 * all, so that full-page route keeps the section's normal title bar.
 */
function OrgsStack(props: { children: React.ReactNode }) {
  const { orgId, groupId } = useParams({ strict: false })
  const canAccessOrgs = usePermissions().can('orgs:read')
  if (!orgId || !canAccessOrgs) return <>{props.children}</>
  return (
    <SectionStack
      list={{ to: '/orgs/$orgId', params: { orgId } }}
      detail={groupId ? { to: '/orgs/$orgId/groups/$groupId', params: { orgId, groupId } } : null}
    >
      {props.children}
    </SectionStack>
  )
}

function OrgsLayout() {
  const { t } = useLingui()
  const { orgId: orgIdFromParams, groupId } = useParams({ strict: false })
  const { first, second } = useRootSearch()

  const hasGroupSelected = !!groupId

  const hasProfileSelected =
    first?.type === 'org_group' ||
    first?.type === 'org_user' ||
    second?.type === 'org_group' ||
    second?.type === 'org_user'

  // Level 1: no org selected → the overview lives on the `orgs.index` route; render it through <Outlet/>.
  // (OrgsOverview owns the zero-orgs empty state, so there's no separate branch here.)
  if (!orgIdFromParams) {
    return (
      <ScrollableContent>
        <ScrollableArea>
          <Outlet />
        </ScrollableArea>
      </ScrollableContent>
    )
  }

  // Level 2+: Org selected — groups panel squashes to icon strip when a group is selected,
  // then the users+report panel slides in from the right (same pattern as JobsLayout).
  // No viewport-driven auto-collapse any more: the nav stack gives each pane the WHOLE screen and decides
  // which one is on it, so both providers stay expanded and the collapse is desktop's business only.
  return (
    <CollapsiblePanelProvider defaultCollapsed={false}>
      {/* The panel row on desktop, the stack's stage on mobile — same div, same classes, see
          NavStackStage. Each pane is wrapped in the level it occupies in the mobile hierarchy; on
          desktop those wrappers are `display: contents` and this lays out exactly as it always did. */}
      <NavStackStage className="flex h-full min-h-0 w-full" style={{ containerType: 'inline-size' }}>
        {/* LEVEL 0 — Groups list (squashes to 52px when a group is selected) */}
        <NavStackScreen
          index={0}
          title={<OrgsRootScreenTitle orgId={orgIdFromParams} />}
          leading={<SectionIconTile icon={NAVIGATION_ICONS.organizations} />}
        >
          <CollapsibleListPanel isOpen={hasGroupSelected} className="print:hidden">
            <CollapsiblePanel expandedWidth="100%" disableTransition>
              <Suspense fallback={<ListBodyPending />}>
                <OrgsGroupsPanel />
              </Suspense>
            </CollapsiblePanel>
          </CollapsibleListPanel>
        </NavStackScreen>

        {/* Right Panel Group — slides in when a group is selected. `NS_PASSTHROUGH_CLASS`: on mobile it stops
            being a box so the two levels inside position against the stage, not against this group's
            (0px / clipped) width. */}
        <SlideInPanel isOpen={hasGroupSelected} innerClassName="flex" className={NS_PASSTHROUGH_CLASS}>
          {/* LEVEL 1 — the group's users. The first screen a group opens onto on mobile. */}
          <NavStackScreen
            index={1}
            title={<GroupScreenTitle />}
            actions={
              orgIdFromParams && groupId ? <GroupScreenActions orgId={orgIdFromParams} groupId={groupId} /> : null
            }
          >
            <CollapsiblePanelProvider cookieName="panel_state_orgs_users" keyboardShortcut="]" defaultCollapsed={false}>
              <StickyPanel className="print:hidden">
                <CollapsiblePanel>
                  <Suspense fallback={<MembersPanelSkeleton />}>
                    {groupId ? (
                      <OrgsUsersPanel orgId={orgIdFromParams} groupId={groupId} />
                    ) : (
                      <PanelSelectionPrompt text={t`Select a functional group to view users`} />
                    )}
                  </Suspense>
                </CollapsiblePanel>
              </StickyPanel>
            </CollapsiblePanelProvider>
          </NavStackScreen>

          <NavStackScreen index={2} title={<ReportScreenTitle />}>
            <ScrollableContent>
              {hasProfileSelected ? (
                // The `$groupId` tabs render here through the report pager, HERE (not in the `$groupId` layout)
                // so the enter animation plays once and never re-animates on tab/group switches.
                hasGroupSelected ? (
                  <Suspense fallback={<ReportPanelSkeleton />}>
                    <OrgGroupReportContent />
                  </Suspense>
                ) : null
              ) : (
                <ScrollableArea>
                  <PanelEmptyState
                    icon={<UsersIcon className="text-muted-foreground size-5" />}
                    title={t`Select a user`}
                    description={t`Select a user from the list to view their profile.`}
                  />
                </ScrollableArea>
              )}
            </ScrollableContent>
          </NavStackScreen>
        </SlideInPanel>
      </NavStackStage>
    </CollapsiblePanelProvider>
  )
}

/**
 * The title of the GROUPS screen (nav stack level 0) — the administration's own name, as the desktop breadcrumb's
 * second crumb shows: inside an administration the groups list IS the section root.
 *
 * Unlike the other section roots it carries its OWN back chevron: the /orgs overview sits ABOVE the stack, and the
 * engine's chevron only exists at depth > 0. A plain navigation, no pop to play, since no level is underneath.
 */
function OrgsRootScreenTitle(props: { orgId: string | undefined }) {
  const { t } = useLingui()
  if (!props.orgId) return null
  return (
    <span className="flex min-w-0 items-center gap-1">
      <ButtonLink
        variant="ghost"
        size="icon"
        aria-label={t`Administrations`}
        className="-ml-2 size-10 shrink-0"
        to="/orgs"
        search={(prev) => clearProfileSelection(prev)}
      >
        <ChevronLeftIcon className="size-5" />
      </ButtonLink>
      <span className="min-w-0 truncate">
        <Suspense fallback={null}>
          <OrgName orgId={props.orgId} />
        </Suspense>
      </span>
    </span>
  )
}

function OrgName(props: { orgId: string }) {
  const { data } = useSuspenseQuery(Route.useRouteContext().orgsListQueryOptions)
  const org = data.find((o) => o.id === props.orgId)
  return <>{org?.name ?? ''}</>
}

/**
 * The title of the GROUP screen (nav stack level 1) — the group's own name, the same thing the desktop
 * breadcrumb's third crumb shows. Suspends on `getGroups`, which the `$groupId` layout has already
 * prefetched, so it fills in with the rest of the screen.
 */
function GroupScreenTitle() {
  const { orgId, groupId } = useParams({ strict: false })
  if (!orgId || !groupId) return null
  return (
    <Suspense fallback={null}>
      <GroupName orgId={orgId} groupId={groupId} />
    </Suspense>
  )
}

function GroupName(props: { orgId: string; groupId: string }) {
  // Above the `$groupId` match, so it reads the OWNING route's context — matched exactly when both ids
  // are params, which is the guard the caller applies.
  const { context } = useMatch({ from: '/_app/orgs/$orgId/groups/$groupId' })
  const { data } = useSuspenseQuery(context.orgGroupsQueryOptions)
  const group = data.find((g) => g.id === props.groupId)
  return <>{group?.name ?? ''}</>
}

/**
 * The GROUP screen's header actions: the group's own report (one push deeper, the same level a tapped user
 * lands on but with the group selected instead of a person — on desktop that pane is always beside the list,
 * so there is nothing to reach for there) and the group's action menu.
 */
function GroupScreenActions(props: { orgId: string; groupId: string }) {
  const { t } = useLingui()
  return (
    <>
      <ButtonLink
        variant="ghost"
        size="icon"
        aria-label={t`Group report`}
        className="size-8"
        to="/orgs/$orgId/groups/$groupId/report"
        params={{ orgId: props.orgId, groupId: props.groupId }}
        search={(prev) => ({
          ...prev,
          first: { type: 'org_group', id: props.groupId },
          second: undefined,
          selected: 'first',
        })}
      >
        <FileTextIcon className="size-4" />
      </ButtonLink>
      <OrgGroupActionMenu group={{ id: props.groupId }} variant="header" />
    </>
  )
}

function OrgsBreadcrumbs() {
  const { t } = useLingui()
  const { orgId, groupId } = useParams({ strict: false })
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink
            className={CRUMB_LINK_CLASS}
            render={
              <Link
                to="/orgs"
                search={(prev) => ({
                  ...clearProfileSelection(prev),
                })}
              >
                {t`Administrations`}
              </Link>
            }
          />
        </BreadcrumbItem>
        {orgId && (
          <Suspense fallback={null}>
            <OrgCrumb orgId={orgId} />
          </Suspense>
        )}
        {orgId && groupId && (
          <Suspense fallback={null}>
            <GroupCrumb orgId={orgId} groupId={groupId} />
          </Suspense>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

function OrgCrumb(props: { orgId: string }) {
  const { data } = useSuspenseQuery(Route.useRouteContext().orgsListQueryOptions)
  const org = data.find((o) => o.id === props.orgId)
  if (!org?.name) return null
  return (
    <>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbLink
          className={CRUMB_LINK_CLASS}
          render={
            <Link
              to="/orgs/$orgId"
              params={{ orgId: props.orgId }}
              search={(prev) => ({
                ...clearProfileSelection(prev),
              })}
            >
              {org.name}
            </Link>
          }
        />
      </BreadcrumbItem>
    </>
  )
}

function GroupCrumb(props: { orgId: string; groupId: string }) {
  // Above the `$groupId` match, so it reads the OWNING route's context — matched exactly when both ids
  // are params, which is the guard the caller applies.
  const { context } = useMatch({ from: '/_app/orgs/$orgId/groups/$groupId' })
  const { data } = useSuspenseQuery(context.orgGroupsQueryOptions)
  const group = data.find((g) => g.id === props.groupId)
  if (!group?.name) return null
  return (
    <>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbLink
          className={CRUMB_LINK_CLASS}
          render={
            <Link
              to="/orgs/$orgId/groups/$groupId/report"
              params={{ orgId: props.orgId, groupId: props.groupId }}
              search={(prev) => ({
                ...clearProfileSelection(prev),
              })}
            >
              {group.name}
            </Link>
          }
        />
      </BreadcrumbItem>
    </>
  )
}

function OrgsGroupsPanel() {
  return (
    <>
      <CollapsiblePanelContent>
        <OrgsGroupsOverview variant="sidebar" />
      </CollapsiblePanelContent>
    </>
  )
}
